<template>
    <div class="container">
        <section class="section" id="install">
            <h1 class="title">Install</h1>
            <div class="content">
                <p>To install <b>vui-ui-table</b>, simply run the following command:</p>
                <pre v-highlightjs class="box pa-0 mb-5"><code class="bash">$ npm i @davidegit/vue-ui-table</code></pre>
                <p>To make it available globally, edit your main.js file as follows:</p>
                <pre v-highlightjs class="box pa-0 mb-5"><code class="javascript">import Vue from &quot;vue&quot;
import VueUiTable from &quot;@davidegit/vue-ui-table&quot;

Vue.use(VueUiTable, { /* config */ })</code></pre>
                <p>Otherwise import the components manually:</p>
                <pre v-highlightjs class="box pa-0"><code class="javascript">import { UiTable, UiRemoteTable, UiTableColumn } from &quot;@davidegit/vue-ui-table&quot;

export default {
	name: &quot;your-component&quot;,
	components: { UiTable, UiRemoteTable, UiTableColumn },
	...
}</code></pre>
            </div>
        </section>
        <section class="section" id="config-object">
            <h1 class="title">Config object</h1>
            <div class="content">
                <p>There are some aspects of the library that you can freely customize:</p>
                <pre v-highlightjs class="box pa-0"><code class="json">{
    theme: {
        icons: {
            sort: "fas fa-angle-up",
            toFirstPage: "fas fa-angle-double-left",
            toPreviousPage: "fas fa-angle-left",
            toNextPage: "fas fa-angle-right",
            toLastPage: "fas fa-angle-double-right",
            component: {
                tag: "ui-icon",
                data: {}
            }
        },
        classes: {
            container: "",
            table: "ui-table",
            bordered: "bordered",
            striped: "striped",
            dense: "dense",
            hoverable: "hoverable",
            sortable: "sortable",
            sorted: "sorted",
            ascSorted: "asc",
            descSorted: "desc"
        },
        loading: {
            type: "spinner",
            spinner: { width: 64, height: 64, color: "#000000" },
            dots: { width: 60, height: 240, color: "#000000" },
            bars: { width: 40, height: 40, color: "#000000" },
        }
    },
    pagination: {
        sort: {
            asc: "asc",
            desc: "desc",
            noOrder: "",
            default: {},
        },
        max: {
            values: [5, 10, 20, 50, 100],
            default: 20,
            component: {
                tag: "ui-max-select",
                data: {},
                label: "items per page"
            }
        },
        page: {
            default: 1,
            component: {
                tag: "ui-page-select",
                data: {},
                label: "page"
            }
        },
        remote: {
            getPaginationParams({ sort, max, page }) { return { params: { max, page, sort }} },
            parseResponsefunction(response) {
                const items = _.get(response.data, "items", [])
                const totalItems = _.get(response.data, "totalItems", 0)
                const page = _.get(response.data, "page", 1)
                const max = _.get(response.data, "max", 20)
                const sort = _.get(response.data, "sort", {})
                return { items, totalItems, page, max, sort }
            }
        }
    },
    emptyMessage: "No items found"
}</code></pre>
            </div>
            <ui-table :items="configObjectDetails" table-class="docs" striped bordered>
                <ui-table-column prop="key" header="Key" fit/>
                <ui-table-column prop="type" header="Type" fit/>
                <ui-table-column prop="description" header="Description"/>
            </ui-table>
        </section>
        <section class="section" id="usage">
            <h1 class="title">Usage</h1>
            <basic :items="items"/>
            <header-prop :items="items" class="mt-5"/>
			<header-slot :items="items" class="mt-5"/>
			<headers-slot :items="items" class="mt-5"/>
            <style-props :items="items" class="mt-5"/>
            <default-slot :items="items" class="mt-5"/>
            <row-slot :items="items" class="mt-5"/>
            <rows-slot :items="items" class="mt-5"/>
            <sorting class="mt-5"/>
            <basic-pagination class="mt-5"/>
            <pagination-object class="mt-5"/>
            <remote-table class="mt-5"/>
        </section>
    </div>
</template>

<script>
    import Basic from "./usage/basic"
    import HeaderProp from "./usage/header-prop"
    import HeaderSlot from "./usage/header-slot"
    import HeadersSlot from "./usage/headers-slot"
    import StyleProps from "./usage/style-props"
    import DefaultSlot from "./usage/default-slot"
    import RowSlot from "./usage/row-slot"
    import RowsSlot from "./usage/rows-slot"
	import Sorting from "./usage/sorting"
	import BasicPagination from "./usage/basic-pagination"
	import PaginationObject from "./usage/pagination-object"
    import RemoteTable from "./usage/remote-table"

    export default {
        name: "home",
        components: { Basic, HeaderProp, HeaderSlot, HeadersSlot, StyleProps, DefaultSlot, RowSlot, RowsSlot, Sorting, BasicPagination, PaginationObject, RemoteTable },
        data() {
            return {
                configObjectDetails: [
                    { key: "theme.icons.sort", type: "String", description: "Icon used for sorting (rotated 180 degrees when descending order)" },
                    { key: "theme.icons.toFirstPage", type: "String", description: "Icon used for 'go to first page'" },
                    { key: "theme.icons.toPreviousPage", type: "String", description: "Icon used for 'go to previous page'" },
                    { key: "theme.icons.toNextPage", type: "String", description: "Icon used for 'go to next page'" },
                    { key: "theme.icons.toLastPage", type: "String", description: "Icon used for 'go to last page'" },
                    { key: "theme.classes.container", type: "String", description: "Class applied to the table container" },
                    { key: "theme.classes.table", type: "String", description: "Class applied to the table" },
                    { key: "theme.classes.bordered", type: "String", description: "Class applied to the table with bordered props set to true" },
                    { key: "theme.classes.striped", type: "String", description: "Class applied to the table with striped props set to true" },
                    { key: "theme.classes.dense", type: "String", description: "Class applied to the table with dense props set to true" },
                    { key: "theme.classes.hoverable", type: "String", description: "Class applied to the table with hoverable props set to true" },
                ],
                items: [
                    {
                        "userId": 1,
                        "id": 1,
                        "title": "sunt aut facere repellat provident occaecati excepturi optio reprehenderit",
                        "body": "quia et suscipit\nsuscipit recusandae consequuntur expedita et cum\nreprehenderit molestiae ut ut quas totam\nnostrum rerum est autem sunt rem eveniet architecto"
                    },
                    {
                        "userId": 1,
                        "id": 2,
                        "title": "qui est esse",
                        "body": "est rerum tempore vitae\nsequi sint nihil reprehenderit dolor beatae ea dolores neque\nfugiat blanditiis voluptate porro vel nihil molestiae ut reiciendis\nqui aperiam non debitis possimus qui neque nisi nulla"
                    },
                    {
                        "userId": 1,
                        "id": 3,
                        "title": "ea molestias quasi exercitationem repellat qui ipsa sit aut",
                        "body": "et iusto sed quo iure\nvoluptatem occaecati omnis eligendi aut ad\nvoluptatem doloribus vel accusantium quis pariatur\nmolestiae porro eius odio et labore et velit aut"
                    },
                    {
                        "userId": 1,
                        "id": 4,
                        "title": "eum et est occaecati",
                        "body": "ullam et saepe reiciendis voluptatem adipisci\nsit amet autem assumenda provident rerum culpa\nquis hic commodi nesciunt rem tenetur doloremque ipsam iure\nquis sunt voluptatem rerum illo velit"
                    },
                    {
                        "userId": 1,
                        "id": 5,
                        "title": "nesciunt quas odio",
                        "body": "repudiandae veniam quaerat sunt sed\nalias aut fugiat sit autem sed est\nvoluptatem omnis possimus esse voluptatibus quis\nest aut tenetur dolor neque"
                    }
                ]

            }
        }
    }
</script>